import React, { useRef, useState, useLayoutEffect } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";

const TooltipWrapper = styled.span`
  position: relative;
  display: inline-block;
`;

const PortalTooltip = styled.div`
  position: absolute;
  background-color: #2e3e3b;
  color: #fff;
  font-size: 0.7rem;
  padding: 4px 8px;
  border-radius: 4px;
  z-index: 3000;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.2s ease;
  text-align: start;
  line-height: 1.7;
  white-space: normal; /* allow text wrapping */
  max-width: 200px; /* adjust as needed */

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #2e3e3b transparent transparent transparent;
  }
`;

const Tooltip = ({ text, children }) => {
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, translateX: "-50%" });

  useLayoutEffect(() => {
    if (visible && wrapperRef.current && tooltipRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const margin = 4;

      let left = rect.left + rect.width / 2 + window.scrollX;
      let translateX = "-50%";

      // Prevent overflow right
      if (left + tooltipRect.width / 2 > window.innerWidth - margin) {
        left = window.innerWidth - margin;
        translateX = "calc(-100% - 4px)";
      }

      // Prevent overflow left
      if (left - tooltipRect.width / 2 < margin) {
        left = margin;
        translateX = "0";
      }

      setCoords({
        top: rect.top + window.scrollY - tooltipRect.height - 8,
        left,
        translateX,
      });
    }
  }, [visible]);

  return (
    <>
      <TooltipWrapper
        ref={wrapperRef}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        tabIndex={0}
      >
        {children}
      </TooltipWrapper>
      {visible &&
        createPortal(
          <PortalTooltip
            ref={tooltipRef}
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              transform: `translateX(${coords.translateX})`,
            }}
          >
            {text}
          </PortalTooltip>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
